import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { AlertService } from '@digilize/shared/feature/modules/alert/src';
import { API_INTERCEPTOR_OPTIONS, ApiInterceptor } from '@digilize/shared/feature/modules/auth/src/lib/api.interceptor';
import { GlobalErrorHandler, GlobalErrorHandlerWithLog } from '@digilize/shared/feature/modules/error-handling';
import { ErrorLanguageInterceptor } from '@digilize/shared/feature/modules/error-handling/src/lib/error-language.interceptor';
import { APP_CONFIG, ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { TranslocoRootModule } from '@plys/src/app/transloco-root.module';
import { BootstrapPrefetchInterceptor } from '@plys/src/application/bootstrap-prefetch.interceptor';
import { CartExpiredInterceptor } from '@plys/src/application/errors/cart-expired.interceptor';
import { ErrorInterceptor } from '@plys/src/application/errors/error.interceptor';
import { ShopAuthInterceptor } from '@plys/src/application/errors/shop-auth.interceptor';
import { ShopLogoComponent } from '@plys/src/ui/shop-logo/shop-logo.component';
import { InlineSVGModule } from 'ng-inline-svg-2';

import { AuthModule } from '../auth/auth.module';
import { appConfig } from '../environments/app.config';
import { environment } from '../environments/environment';
import { CookieConsentBannerComponent } from '../ui/cookie-consent-banner/cookie-consent-banner.component';
import { ResetPasswordDialogComponent } from '../ui/reset-password-dialog/reset-password-dialog.component';
import { TimerComponent } from '../ui/timer/timer.component';
import { CheckoutWrapperComponent } from './../ui/checkout/checkout-wrapper/checkout-wrapper.component';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { NextStepPreloadStrategy } from './next-step-preload.strategy';

/* The interceptor itself, not the lib's index: that barrel re-exports the login module and its
   eight dialogs, which is how the editor bundle and the Material date pickers reached this app's
   initial bundle. */

/**
 * Everything the buyer sees before a step does: the shell, its header and the banners. Every page
 * is standalone and reached through `app.routes.ts`, so what a step needs travels with the step —
 * only the two the checkout can open on are pulled into this bundle, by being referenced eagerly
 * there.
 */
@NgModule({
  /* The state debugger is deliberately absent: it is standalone and loaded on demand by
     `CheckoutWrapperComponent`, so production no longer carries a staging-only panel. */
  declarations: [AppComponent, CheckoutWrapperComponent, CookieConsentBannerComponent, ResetPasswordDialogComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes, { preloadingStrategy: NextStepPreloadStrategy }),
    HttpClientModule,
    InlineSVGModule.forRoot({ baseUrl: '/' }),
    /* The six Material pieces the shell itself needs, rather than `SharedUiMaterialModule`: that
       barrel imports 27 modules — the date/time pickers, moment, chips, the slider — and importing
       it here put every one of them in the initial bundle. A step that needs more imports it. */
    MatDialogModule,
    MatIconModule,
    MatSnackBarModule,
    ReactiveFormsModule,
    ShopLogoComponent,
    TimerComponent,
    TranslocoRootModule,
    AuthModule.forRoot('jwt', environment.recaptchaSiteKey),
  ],
  providers: [
    AlertService,
    {
      provide: ErrorHandler,
      useClass: !environment.development ? GlobalErrorHandlerWithLog : GlobalErrorHandler,
    },
    {
      provide: ENV_CONFIG,
      useValue: environment,
    },
    {
      provide: APP_CONFIG,
      useValue: appConfig,
    },
    {
      provide: API_INTERCEPTOR_OPTIONS,
      useValue: {
        /* PAUL-3246: renew on the server's refusal, not only on a clock the frontend keeps. */
        reactiveTokenRefresh: true,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ShopAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CartExpiredInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorLanguageInterceptor,
      multi: true,
    },
    /*
     * Below the error interceptors, so a prefetched answer never has to pretend to be an error, and
     * above `ApiInterceptor` so the request already carries the `Authorization` header it compares —
     * the shop's own reads use absolute URLs, which `ApiInterceptor` leaves alone anyway.
     */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BootstrapPrefetchInterceptor,
      multi: true,
    },
    /*
     * Last, and therefore closest to the backend: errors bubble back up the chain in reverse, so this
     * has to sit below `ErrorInterceptor` to see a 401 at all. Above it, `ErrorInterceptor` turned every
     * 401 into `EMPTY` first (clearing the session), and the renewal-and-replay below never ran
     * (PAUL-3246). A replay that succeeds now means `ErrorInterceptor` sees no error; one that fails
     * twice reaches it exactly as before. Rewriting the host is the last request-direction step anyway,
     * which is the order pauly-order2seat documents for the same three-way split.
     */
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
